import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import Reveal from '../../components/Reveal';
import Lightbox from '../../components/Lightbox';
import GalleryGrid from '../../components/GalleryGrid';
import MediaCarousel from '../../components/MediaCarousel';
import FacebookPostEmbed from '../../components/FacebookPostEmbed';
import { PageLoader, ErrorState, Breadcrumbs } from '../../components/ui';
import { useAsync } from '../../lib/useAsync';
import { sanitize } from '../../lib/sanitize';
import { eventsApi, recapsApi } from '../../api/resources';
import { assetUrl } from '../../api/client';
import { formatDate, formatDateTime, truncate, statusMeta } from '../../lib/format';

function formatRange(starts, ends) {
  if (!starts) return '';
  const start = formatDateTime(starts);
  if (!ends) return start;
  const s = new Date(starts);
  const e = new Date(ends);
  if (Number.isNaN(e.getTime())) return start;
  const sameDay = s.toDateString() === e.toDateString();
  const endStr = sameDay
    ? e.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : formatDateTime(ends);
  return `${start} – ${endStr}`;
}

/** Photo grid for a single recap; clicking a tile opens the shared lightbox. */
function RecapGallery({ media, onOpen }) {
  if (!media?.length) return null;
  return (
    <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
      {media.map((m, i) => (
        <button
          key={m.id}
          type="button"
          onClick={() => onOpen(i)}
          className="group relative aspect-[4/3] overflow-hidden rounded-xl bg-cream-200 shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-maroon-400"
          aria-label={m.alt_text ? `View photo: ${m.alt_text}` : `View photo ${i + 1}`}
        >
          <img
            src={assetUrl(m.url)}
            alt={m.alt_text || ''}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute inset-0 bg-maroon-950/0 transition-colors group-hover:bg-maroon-950/15" />
        </button>
      ))}
    </div>
  );
}

/** "What happened" — timeline of published recaps. Hidden when there are none. */
function RecapTimeline({ eventSlug }) {
  const { data } = useAsync(() => recapsApi.list(eventSlug, { per_page: 50 }), [eventSlug]);
  // { images, recapIndex } → drives the shared lightbox across all recaps.
  const [viewer, setViewer] = useState(null);

  const recaps = data?.items || [];
  if (recaps.length === 0) return null;

  return (
    <section className="mt-14 border-t border-cream-300 pt-10" aria-labelledby="what-happened">
      <Reveal>
        <p className="eyebrow mb-1">In Case You Missed It</p>
        <h2 id="what-happened" className="heading-brand text-2xl sm:text-3xl">
          What happened
        </h2>
      </Reveal>

      <ol className="mt-8 space-y-8">
        {recaps.map((recap, ri) => (
          <Reveal as="li" key={recap.id} delay={Math.min(ri * 60, 240)}>
            <article className="relative rounded-2xl border border-cream-300 bg-surface-card p-6 shadow-card sm:p-8">
              <div className="mb-3 flex flex-wrap items-center gap-3">
                <span className="pill-gold">
                  {recap.published_at ? formatDate(recap.published_at) : 'Recap'}
                </span>
                {recap.created_by && (
                  <span className="text-xs text-ink-muted">by {recap.created_by}</span>
                )}
              </div>
              {recap.title && (
                <h3 className="font-display text-xl font-bold text-ink">{recap.title}</h3>
              )}
              <div
                className="prose-zonta mt-3 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: sanitize(recap.body) }}
              />
              <RecapGallery
                media={recap.media}
                onOpen={(imgIndex) => setViewer({ recapIndex: ri, imgIndex })}
              />
            </article>
          </Reveal>
        ))}
      </ol>

      <Lightbox
        images={
          viewer != null
            ? (recaps[viewer.recapIndex].media || []).map((m) => ({
                url: assetUrl(m.url),
                alt_text: m.alt_text,
              }))
            : []
        }
        index={viewer?.imgIndex ?? -1}
        onIndexChange={(i) => setViewer((v) => (v ? { ...v, imgIndex: i } : v))}
        onClose={() => setViewer(null)}
      />
    </section>
  );
}

export default function EventDetail() {
  const { slug } = useParams();
  const { data: event, loading, error, reload } = useAsync(() => eventsApi.get(slug), [slug]);
  const [carouselIndex, setCarouselIndex] = useState(null);

  if (loading) return <PageLoader label="Loading event…" />;
  if (error) return <div className="container-page py-16"><ErrorState error={error} onRetry={reload} /></div>;
  if (!event) return null;

  const img = assetUrl(event.banner_image_path) || assetUrl(event.first_image_url);
  const banner = img || '/img/story-b.jpg';
  const gallery = event.gallery || [];
  // Facebook post embeds are shown inline below the body; everything else
  // stays in the photo grid + carousel (both get the same filtered array, so
  // carousel indexes line up with the grid).
  const posts = gallery.filter((g) => g.provider === 'facebook_post');
  const media = gallery.filter((g) => g.provider !== 'facebook_post');
  const status = event.status ? statusMeta(event.status) : null;

  return (
    <>
      <Seo
        title={event.title}
        description={truncate(event.description, 160)}
        image={img}
        type="article"
      />

      <article className="pb-16">
        <header className="relative overflow-hidden">
          <img
            src={banner}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="hero-overlay" />
          <div className="container-page relative max-w-4xl py-20 sm:py-28">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              {status && <span className={`pill ${status.cls}`}>{status.label}</span>}
              {event.starts_at && (
                <span className="text-sm text-cream-200">{formatRange(event.starts_at, event.ends_at)}</span>
              )}
            </div>
            <h1 className="font-serif text-3xl font-bold leading-tight text-white sm:text-5xl">
              {event.title}
            </h1>
          </div>
        </header>

        <div className="container-page max-w-3xl pt-8">
          <Breadcrumbs
            items={[{ label: 'Home', to: '/' }, { label: 'Events', to: '/events' }, { label: event.title }]}
          />

          <div className="mt-4 flex flex-wrap gap-4 text-sm text-ink-muted">
            {event.location && (
              <span>
                <span className="font-medium text-ink-soft">Location:</span> {event.location}
              </span>
            )}
            {event.capacity != null && (
              <span>
                <span className="font-medium text-ink-soft">Capacity:</span> {event.capacity}
              </span>
            )}
          </div>

          <div
            className="rich-content mt-6"
            dangerouslySetInnerHTML={{ __html: sanitize(event.description) || '<p>No details available.</p>' }}
          />

          {posts.length > 0 && (
            <section className="mt-10" aria-labelledby="event-facebook-heading">
              <h2 id="event-facebook-heading" className="heading-brand text-xl">
                From our Facebook Page
              </h2>
              <div className="mt-4 space-y-6">
                {posts.map((p) => (
                  <FacebookPostEmbed key={p.id} embedUrl={p.embed_url} url={p.url} title={`Facebook post: ${event.title}`} />
                ))}
              </div>
            </section>
          )}

          {media.length > 0 && (
            <section className="mt-10" aria-labelledby="event-gallery-heading">
              <h2 id="event-gallery-heading" className="heading-brand text-xl">
                Gallery
              </h2>
              <p className="mt-1 text-sm text-ink-muted">
                {media.length} {media.length === 1 ? 'item' : 'items'}. Select any to view.
              </p>
              <div className="mt-4">
                <GalleryGrid items={media} onOpen={setCarouselIndex} />
              </div>
            </section>
          )}

          <RecapTimeline eventSlug={slug} />

          <div className="mt-10">
            <Link to="/events" className="btn-ghost">
              ← Back to all events
            </Link>
          </div>
        </div>
      </article>

      <MediaCarousel
        items={media}
        index={carouselIndex}
        onClose={() => setCarouselIndex(null)}
        onIndexChange={setCarouselIndex}
      />
    </>
  );
}
